import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, User, FileText } from "lucide-react";

interface CustomerIdentificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (data: { cpf?: string; nome?: string } | null) => void;
  isLoading?: boolean;
}

// CPF mask: XXX.XXX.XXX-XX
function formatCPF(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

// Validate CPF check digits
function isValidCPF(cpf: string): boolean {
  const digits = cpf.replace(/\D/g, "");
  if (digits.length !== 11) return false;
  
  // Check for known invalid patterns (all same digits)
  if (/^(\d)\1{10}$/.test(digits)) return false;
  
  // First check digit
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(digits[i]) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10) remainder = 0;
  if (remainder !== parseInt(digits[9])) return false;
  
  // Second check digit
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(digits[i]) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10) remainder = 0;
  if (remainder !== parseInt(digits[10])) return false;
  
  return true;
}

export function CustomerIdentificationDialog({
  open,
  onOpenChange,
  onConfirm,
  isLoading = false,
}: CustomerIdentificationDialogProps) {
  const [cpf, setCpf] = useState("");
  const [nome, setNome] = useState("");
  const [cpfError, setCpfError] = useState("");

  const handleCpfChange = (value: string) => {
    const formatted = formatCPF(value);
    setCpf(formatted);
    setCpfError("");
  };

  const handleConfirmWithCpf = () => {
    const digits = cpf.replace(/\D/g, "");
    
    if (digits.length > 0 && digits.length !== 11) {
      setCpfError("CPF deve ter 11 dígitos");
      return;
    }
    
    if (digits.length === 11 && !isValidCPF(digits)) {
      setCpfError("CPF inválido");
      return;
    }
    
    onConfirm({
      cpf: digits.length === 11 ? digits : undefined,
      nome: nome.trim() || undefined,
    });
    
    // Reset form
    setCpf("");
    setNome("");
    setCpfError("");
  };

  const handleSkip = () => {
    onConfirm(null);
    // Reset form
    setCpf("");
    setNome("");
    setCpfError("");
  };

  // Handle dialog state change - only close when explicitly requested
  const handleOpenChange = (nextOpen: boolean) => {
    // If trying to open, just propagate (don't auto-close)
    if (nextOpen) {
      onOpenChange(true);
      return;
    }
    
    // If trying to close, block when loading
    if (!isLoading) {
      onOpenChange(false);
      setCpf("");
      setNome("");
      setCpfError("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Identificar Cliente na NFC-e?
          </DialogTitle>
          <DialogDescription>
            O cliente pode solicitar a inclusão do CPF na nota fiscal.
            Isso é opcional e pode ser útil para garantia ou dedução de IR.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="cpf">CPF do Cliente</Label>
            <Input
              id="cpf"
              placeholder="000.000.000-00"
              value={cpf}
              onChange={(e) => handleCpfChange(e.target.value)}
              disabled={isLoading}
              maxLength={14}
              className={cpfError ? "border-destructive" : ""}
            />
            {cpfError && (
              <p className="text-sm text-destructive">{cpfError}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="nome">Nome do Cliente (opcional)</Label>
            <Input
              id="nome"
              placeholder="Nome completo"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              disabled={isLoading}
              maxLength={60}
            />
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleSkip}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            Não Identificar
          </Button>
          <Button
            onClick={handleConfirmWithCpf}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Emitindo...
              </>
            ) : (
              <>
                <User className="h-4 w-4 mr-2" />
                {cpf.replace(/\D/g, "").length === 11 ? "Emitir com CPF" : "Emitir sem CPF"}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
